import * as Sentry from '@sentry/react'
import { useEffect } from 'react'
import { get } from '../api/api'
import { useAppStore } from '../store/appStore'
import { captureRequestOrigin, isRequestCurrent } from '../store/sessionActions'

/** Flags the frontend reads; the backend resolves them per user (MD-1643). */
export type FeatureFlagKey = 'assistant' | 'chat' | 'analytics' | 'live_translation'

/** Loads the signed-in user's flags from `/me/flags` once their session is active. */
export const useFeatureFlagsInit = (): void => {
    const userId = useAppStore((state) => (state.session.tag === 'active' ? state.session.user.id : null))

    useEffect(() => {
        if (!userId) return
        // Flags belong to one user: none of a previous user's may stay on while this user's load or if they fail.
        useAppStore.setState({ featureFlags: {} })
        const requestOrigin = captureRequestOrigin()
        let superseded = false

        get<Record<string, boolean>>('/me/flags')
            .then((flags) => {
                // Another account or a new session may have taken over while this was in flight.
                if (superseded || !isRequestCurrent(requestOrigin)) return
                useAppStore.setState({ featureFlags: flags })
            })
            .catch((error: unknown) => {
                // Flags only switch features on, so a failed fetch leaves them all off instead of blocking the app.
                // Chat, analytics and live translation then vanish for this user: report it so we see it before they do.
                console.warn('Failed to fetch feature flags:', error)
                Sentry.captureException(error)
            })

        return () => {
            superseded = true
        }
    }, [userId])
}

/** Whether the backend serves `key` as on for the signed-in user; off until the flags are loaded. */
export const useFeatureFlag = (key: FeatureFlagKey): boolean => useAppStore((state) => state.featureFlags[key] === true)
